import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge, IconButton, Icons, iconSize, palette } from '@munifocus/ui';
import { useConversations } from '../lib/chat-api';

/**
 * The topbar messages icon and its unread badge.
 *
 * Navigates rather than opening a panel: chat is a place officers go and stay,
 * unlike the bell, which is a glance. Making it a dropdown would mean composing
 * a reply inside a menu that any outside click dismisses.
 *
 * The count comes from the same query the Messages page reads, so the badge and
 * the list can never disagree — and the socket updates both at once.
 */
export function ChatIconButton({ onDark = false }: { onDark?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const conversations = useConversations();

  const unread = conversations.data?.totalUnread ?? 0;

  return (
    <IconButton
      onClick={() => navigate('/messages')}
      aria-label={
        unread > 0 ? t('common.chat.titleUnread', { count: unread }) : t('common.chat.title')
      }
      sx={{ color: onDark ? palette.text.onDark : palette.text.primary }}
    >
      <Badge
        badgeContent={unread}
        max={9}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: palette.brand.accent,
            color: palette.text.onDark,
          },
        }}
      >
        <Icons.MessageSquare size={iconSize.lg} aria-hidden />
      </Badge>
    </IconButton>
  );
}
